import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import { holdWalletBalance } from '@/features/wallet/balanceHold.js';
import { invalidateClaimState } from '@/features/promo/api.js';
import { mapPlayError, type PlayErrorKind } from './playError.js';
import { outcomeUnknown, playUntilKnown, useUnmountSignal } from './playUntilKnown.js';
import type { GameSelection, PlayEnvelope } from './types.js';

export type PlayPhase = 'idle' | 'playing' | 'revealed';

export type { PlayErrorKind } from './playError.js';

export interface UseOriginalsPlay {
  phase: PlayPhase;
  envelope: PlayEnvelope | null;
  error: string | null;
  errorKind: PlayErrorKind | null;
  play: (betAmount: number, params?: Record<string, string | number>, selection?: GameSelection) => Promise<void>;
  onRevealComplete: () => void;
  reset: () => void;
}

/**
 * Drives the originals play loop as a small state machine.
 *
 * The scene — not this hook — advances `playing → revealed`: `play()` locks the
 * envelope and stays in `playing` so the scene can animate to the result, then
 * calls `onRevealComplete()` to settle the round.
 */
export function useOriginalsPlay(gameCode: string): UseOriginalsPlay {
  const { t } = useTranslation('originals');
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<PlayPhase>('idle');
  const [envelope, setEnvelope] = useState<PlayEnvelope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<PlayErrorKind | null>(null);
  const inFlight = useRef(false);
  const unmountSignal = useUnmountSignal();
  const releaseBalance = useRef<(() => void) | null>(null);
  const releaseBalanceHold = useCallback(() => {
    releaseBalance.current?.();
    releaseBalance.current = null;
  }, []);
  useEffect(() => releaseBalanceHold, [releaseBalanceHold]);

  const play = useCallback(
    async (betAmount: number, params?: Record<string, string | number>, selection?: GameSelection) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError(null);
      setErrorKind(null);
      setEnvelope(null);
      setPhase('playing');
      // The wallet books stake and payout at once and pushes the new balance
      // over SSE before the scene has shown anything; hold it until the reveal.
      releaseBalanceHold();
      releaseBalance.current = holdWalletBalance();
      try {
        // One key per bet, kept across every resend of it: an unanswered or
        // still-confirming bet is asked again under the same key until the
        // server says what happened to it.
        const result = await playUntilKnown(
          { gameCode, betAmount, params, selection, idempotencyKey: crypto.randomUUID() },
          {
            signal: unmountSignal.current,
            onConfirming: () => setError(t('ORIG-020', { ns: 'errors' })),
          },
        );
        setError(null);
        setEnvelope(result);
        // Balance refresh is deferred to onRevealComplete — refetching here would
        // surface the post-win balance mid-animation and spoil the reveal.
      } catch (err) {
        // A failed play must always settle into a re-bettable idle state with a
        // clear, kind-tagged message — never the cryptic "could not load game"
        // (which implies the page never loaded) and never an uncaught rejection.
        const mapped = mapPlayError(err);
        setError(t(mapped.key, { ns: mapped.ns }));
        setErrorKind(mapped.kind);
        setPhase('idle');
        releaseBalanceHold();
        // Still unknown after the last resend: the stake may have been taken,
        // so the balance is re-read rather than left showing the pre-bet amount.
        if (outcomeUnknown(err)) void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      } finally {
        inFlight.current = false;
      }
    },
    [gameCode, queryClient, t, releaseBalanceHold],
  );

  const onRevealComplete = useCallback(() => {
    setPhase('revealed');
    releaseBalanceHold();
    // Settle the on-screen balance only once the player has seen the result, and
    // refresh promo turnover (the bet counts toward any active bonus) so the
    // Promo page isn't stale when the player switches to it.
    void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
    invalidateClaimState(queryClient);
  }, [queryClient, releaseBalanceHold]);

  const reset = useCallback(() => {
    releaseBalanceHold();
    setPhase('idle');
    setEnvelope(null);
    setError(null);
    setErrorKind(null);
  }, [releaseBalanceHold]);

  return { phase, envelope, error, errorKind, play, onRevealComplete, reset };
}
